import React, { useMemo, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import {
    Button,
    Flex,
    Card,
    Skeleton,
    Empty,
    Typography,
    Table,
} from '@app/ui';
import { SocialBenchmarkingContext } from './SocialBenchmarkingContext';
import ResizableTable from '@app/components/common/ResizableTable';
import downloadCSV from '@app/utils/downloadCsv';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import buildHeaders from './socBenchTableColumns';
import { User } from '@app/types/api';
// import * as format from '@app/utils/formatters';

// const { Text } = Typography;

interface SocialBenchmarkingTableProps {
    socialBenchmarking: {
        accountGroups: any[];
        favoritesList: any[];
        isLoading: boolean;
        leagues: any[];
        periodStartDate: string;
        periodEndDate: string;
        hasNoData: boolean;
    };
    sendNotifications: (message: string, options: any) => void;
    isLoading: boolean;
    user: User;
}

const SocialBenchmarkingTable: React.FC<SocialBenchmarkingTableProps> = ({
    socialBenchmarking,
    sendNotifications,
    isLoading,
    user,
}) => {
    const { t } = useTranslation('benchmarking');
    const antdTableRef = React.useRef(null);
    const { accountGroups, favoritesList, leagues } = socialBenchmarking;

    const {
        socialMetric,
        leagueFilterValue,
        isFollowerMetricCurrent,
        isPostMetricCurrent,
        periodicity,
        setCurrentPageData,
        currentPage,
        setCurrentPage,
        currentSocialMetricFilterValue,
    } = useContext(SocialBenchmarkingContext);

    const title = useMemo(() => {
        if (!accountGroups.length) return null;
        if (!leagues && !favoritesList) return null;
        const showLeagues = !Number.isNaN(parseInt(leagueFilterValue));
        if (showLeagues) {
            const selectedLeague = leagues.find(l => l.id == leagueFilterValue);
            if (!selectedLeague) return null;
            return t('socialBenchmarking.table.title.league', {
                league: selectedLeague.name,
                metric: socialMetric,
            });
        } else {
            return t('socialBenchmarking.table.title.favorites', {
                metric: socialMetric,
            });
        }
    }, [
        socialMetric,
        accountGroups,
        leagues,
        favoritesList,
        leagueFilterValue,
        t,
    ]);

    const handleTableChange = (pagination: any) => {
        const { current, pageSize } = pagination;
        setCurrentPage(current);

        // Calculate the data for the current page
        const startIndex = (current - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const sortedAccountGroups = accountGroups.sort(
            (a, b) => b.itemCount - a.itemCount,
        );
        const pageData = sortedAccountGroups.slice(startIndex, endIndex);
        setCurrentPageData(pageData);
    };

    // Initialize current page data when accountGroups change
    useEffect(() => {
        if (accountGroups.length > 0) {
            const sortedAccountGroups = accountGroups.sort(
                (a, b) => b.itemCount - a.itemCount,
            );
            const pageData = sortedAccountGroups.slice(0, 10);
            setCurrentPageData(pageData);
        }
    }, [accountGroups]);

    // Reset page to 1 when accountGroups change (when filters are applied)
    useEffect(() => {
        setCurrentPage(1);
    }, [accountGroups, setCurrentPage]);

    // Dynamic headers based on currentSocialMetricFilterValue (similar to v1)
    const {
        countPerDayAccessor,
        countPerDayHeader,
        countPerDayUpdatedAccessor,
        countPerDayUpdatedHeader,
    } = useMemo(() => {
        let countPerDayAccessor = null;
        let countPerDayHeader = null;
        let countPerDayUpdatedAccessor = null;
        let countPerDayUpdatedHeader = null;

        switch (currentSocialMetricFilterValue) {
            case 'post':
                countPerDayAccessor = 'itemCountPerDay';
                countPerDayHeader = t(
                    'socialBenchmarking.table.columns.metricPerDay',
                    { metric: socialMetric },
                );
                countPerDayUpdatedAccessor = 'postVolumeChange';
                countPerDayUpdatedHeader = t(
                    'socialBenchmarking.table.columns.periodicity',
                    { periodicity },
                );
                break;
            case 'engagement':
                countPerDayAccessor = 'itemCountPerDay';
                countPerDayHeader = t(
                    'socialBenchmarking.table.columns.metricPerDay',
                    { metric: socialMetric },
                );
                countPerDayUpdatedAccessor = 'engagementsPerPost';
                countPerDayUpdatedHeader = t(
                    'socialBenchmarking.table.columns.metricPerPost',
                    { metric: socialMetric },
                );
                break;
            case 'follower':
                countPerDayAccessor = 'newFollowers';
                countPerDayHeader = t(
                    'socialBenchmarking.table.columns.changeInFollowers',
                );
                break;
            default:
                break;
        }

        return {
            countPerDayAccessor,
            countPerDayHeader,
            countPerDayUpdatedAccessor,
            countPerDayUpdatedHeader,
        };
    }, [socialMetric, periodicity, currentSocialMetricFilterValue, t]);

    const columns = useMemo(() => {
        return buildHeaders({
            socialMetric,
            isFollowerMetricCurrent,
            isPostMetricCurrent,
            countPerDayAccessor,
            countPerDayHeader,
            countPerDayUpdatedAccessor,
            countPerDayUpdatedHeader,
            t,
        });
    }, [
        socialMetric,
        isFollowerMetricCurrent,
        isPostMetricCurrent,
        periodicity,
        countPerDayAccessor,
        countPerDayHeader,
        countPerDayUpdatedAccessor,
        countPerDayUpdatedHeader,
        t,
    ]);

    const summary = useCallback(() => {
        return (
            <Table.Summary.Row>
                {columns.map((column, index) => {
                    const { dataIndex: accessor, render } = column;

                    if (index === 0) {
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell first-cell"
                            >
                                {t('socialBenchmarking.table.summary.average')}
                            </Table.Summary.Cell>
                        );
                    }

                    if (index === 1 || index === 2) {
                        // Skip Account Group and Platform columns
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell"
                            />
                        );
                    }

                    // Calculate average for the last 4 columns (index 3, 4, 5, 6)
                    const fullData = accountGroups || [];
                    const values = fullData.map(row => row[accessor]);
                    const filtered = values.filter(
                        v => v !== undefined && v !== null && !isNaN(v),
                    );

                    if (filtered.length === 0) {
                        return (
                            <Table.Summary.Cell
                                index={index}
                                key={column.key}
                                className="summary-cell"
                            />
                        );
                    }

                    const average = Math.round(
                        filtered.reduce((sum, val) => sum + val, 0) /
                            filtered.length,
                    );

                    // Apply the same render function as the column
                    const renderedSummary =
                        typeof render === 'function'
                            ? (render as any)(average, {})
                            : average;

                    return (
                        <Table.Summary.Cell
                            index={index}
                            key={column.key}
                            className={`summary-cell ${index === columns.length - 1 ? 'last-cell' : ''}`}
                        >
                            {renderedSummary}
                        </Table.Summary.Cell>
                    );
                })}
            </Table.Summary.Row>
        );
    }, [columns, accountGroups]);

    const _downloadAntdTableCsv = () => {
        const { csvDelimiter: delimiter } = user;

        if (!accountGroups || !columns) return;

        const rows = accountGroups.map(row => {
            const result = {};
            columns.forEach(col => {
                const accessor = col.dataIndex;
                const header = col.title;
                let value = row[accessor];

                // Apply render function if it exists (and isn't just JSX)
                if (typeof col.render === 'function') {
                    try {
                        // Check if there's a special render function for export
                        if (
                            col.renderForExport &&
                            typeof col.renderForExport === 'function'
                        ) {
                            const renderForExport = col.renderForExport as (
                                value: any,
                            ) => any;
                            value = renderForExport(value);
                        } else {
                            // Cast render function to accept any type since we don't know the exact type
                            const render = col.render as (
                                value: any,
                                row: any,
                            ) => any;
                            const rendered = render(value, row);
                            if (
                                typeof rendered === 'string' ||
                                typeof rendered === 'number'
                            ) {
                                value = rendered;
                            }
                        }
                    } catch {
                        // fallback to raw value
                    }
                }

                result[header] = value;
            });
            return result;
        });

        const csv = Papa.unparse(rows, { delimiter });
        downloadCSV(csv, 'social_benchmarking_export');
        sendNotifications(t('socialBenchmarking.table.export.success'), {
            context: 'success',
        });
    };

    const titleContent = (
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <Flex
                gap={12}
                justify="flex-start"
                align="center"
                style={{
                    height: 'fit-content',
                    fontSize: 20,
                }}
            >
                {title}
            </Flex>
        </Flex>
    );

    return (
        <Card
            style={{
                marginTop: 24,
            }}
        >
            <Flex
                align="center"
                style={{
                    marginBottom: 24,
                }}
            >
                <Flex flex={1}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                        }}
                    >
                        {titleContent}
                    </Typography.Title>
                </Flex>

                <Flex>
                    <Button
                        onClick={_downloadAntdTableCsv}
                        className="-m-l-2"
                        type="primary"
                        color="primary"
                        variant="solid"
                        disabled={isLoading}
                    >
                        <i className="fa-regular fa-download -m-r-1" />
                        {t('socialBenchmarking.table.export.downloadCsv')}
                    </Button>
                </Flex>
            </Flex>
            <div className="data-explorer-table" style={{ width: '100%' }}>
                <ErrorBoundary FallbackComponent={CrashedComponent}>
                    <ResizableTable
                        ref={antdTableRef}
                        columns={columns as any}
                        dataSource={accountGroups}
                        pagination={{
                            current: currentPage,
                            pageSize: 10,
                            showSizeChanger: false,
                        }}
                        onChange={handleTableChange}
                        locale={{
                            emptyText: isLoading ? (
                                <Skeleton active paragraph={{ rows: 10 }} />
                            ) : (
                                <Empty />
                            ),
                        }}
                        bordered
                        rowClassName={(_, index) =>
                            index % 2 === 0
                                ? 'table-row-light'
                                : 'table-row-dark'
                        }
                        summary={summary}
                        showSorterTooltip={false}
                        style={{ width: '100%' }}
                        scroll={{ x: 'max-content' }}
                    />
                </ErrorBoundary>
            </div>
        </Card>
    );
};

export default SocialBenchmarkingTable;
